import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Box } from '@mui/material';
import { teamApi } from '../services/api';
import { Team } from '../models';

interface TeamAutocompleteProps {
  value: Team | null;
  onChange: (value: Team | null) => void;
  error?: boolean;
  helperText?: string;
  seriesId?: number | null;
  disabled?: boolean;
}

const TeamAutocomplete: React.FC<TeamAutocompleteProps> = ({
  value,
  onChange,
  error,
  helperText,
  seriesId,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<Team[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Handle input change with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(inputValue);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch teams when search term changes
  useEffect(() => {
    let active = true;

    const fetchTeams = async () => {
      if (disabled) {
        setOptions([]);
        return;
      }

      setLoading(true);
      try {
        const params: any = {
          search: searchTerm,
          page_size: 20,
        };
        
        // If series is selected, filter teams by that series
        if (seriesId) {
          params.series = seriesId;
        }

        const response = await teamApi.getAll(params);
        if (active) {
          setOptions(response.data.results);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    fetchTeams();

    return () => {
      active = false;
    };
  }, [searchTerm, seriesId, disabled]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={value}
      onChange={(event, newValue) => {
        onChange(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      options={options}
      loading={loading}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      disabled={disabled}
      size='small'
      renderOption={(props, option) => (
        <Box component="li" sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...props}>
          {option.image && (
            <img
              loading="lazy"
              width="20"
              height="20"
              src={option.image}
              alt={option.name}
              style={{ borderRadius: '4px' }}
            />
          )}
          {option.name}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Team"
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default TeamAutocomplete;
